import React from "react";
import { clsx } from "clsx";
import { ArrowLeft } from "lucide-react";

export interface PageHeaderProps {
    title: string;
    subtitle?: string;
    /** Shows a "Back" button above the title when provided. */
    onBack?: () => void;
    /** Label for the back button. Defaults to "Back" — pass a translated string in apps using i18n. */
    backLabel?: string;
    /** Rendered on the right side of the title row (e.g. a button). */
    actions?: React.ReactNode;
    className?: string;
}

/**
 * Standard page heading: an optional back button, a title/subtitle, and optional
 * right-aligned actions. Replaces the hand-rolled back-button + <h1> block repeated
 * across pages.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    subtitle,
    onBack,
    backLabel = "Back",
    actions,
    className,
}) => {
    return (
        <div className={clsx("mb-6", className)}>
            {onBack && (
                <button
                    type="button"
                    onClick={onBack}
                    className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-secondary transition-colors hover:text-primary dark:hover:text-gold-light"
                >
                    <ArrowLeft size={16} className="rtl:rotate-180" />
                    {backLabel}
                </button>
            )}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-ink dark:text-white sm:text-3xl">
                        {title}
                    </h1>
                    <div className="mt-2 h-[3px] w-10 rounded-full bg-gradient-to-r from-gold to-gold-light" />
                    {subtitle && (
                        <p className="mt-2 text-xs text-neutral-secondary sm:text-sm">
                            {subtitle}
                        </p>
                    )}
                </div>
                {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
            </div>
        </div>
    );
};
